import React, { useCallback, useEffect } from 'react'
import { selectSignup } from 'selectors/auth'
import { useForm } from 'components/utils/useForm'
import { signup, dismissSignupError } from 'actions/auth'
import filter from 'lodash/filter'
import Button from 'components/utils/Button'
import FormGroup from 'components/utils/FormGroup'
import FormLabel from 'components/utils/FormLabel'
import FormControl from 'components/utils/FormControl'
import FormHelp from 'components/utils/FormHelp'
import Alert from 'components/utils/Alert'
import { useAppSelector, useAppDispatch } from 'store/hooks'

const validators = {
  username: {
    errors: {
      'Choose a username': (username: string) => username.length === 0,
    },
  },
  email: {
    errors: {
      'Enter your email address': (email: string) => email.length === 0,
      "That doesn't look like an email address": (email: string) =>
        !/^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i.test(
          email
        ),
    },
  },
  password: {
    errors: {
      'Choose a password': (password: string) => password.length === 0,
      'Your password should be at least 8 characters long': (password: string) => password.length < 8,
    },
  },
  confirmPassword: {
    errors: {
      "Passwords don't match": (password: string, state: { fields: Record<string, { value: string }> }) =>
        password !== state.fields.password?.value,
    },
  },
}

const SignupForm = () => {
  const { signup: signupState, errors } = useAppSelector(selectSignup)
  const dispatch = useAppDispatch()
  const su = (signupState || {}) as { isFetching?: boolean; inviteToken?: string }

  const onSubmit = useCallback(
    (values: Record<string, string>) => {
      dispatch(signup(values as any) as any)
    },
    [dispatch]
  )

  const { handleFieldChange, handleSubmit, fieldValue, fieldMessage, validationState, formInvalid, setFormState } =
    useForm({
      initialFields: { username: '', email: '', password: '', confirmPassword: '', inviteToken: '' },
      validators,
      onSubmit,
    })

  useEffect(() => {
    if (!su.inviteToken) {
      window.location.href = '/login'
    }
    if (su.inviteToken) {
      setFormState((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          inviteToken: { value: su.inviteToken!, state: null, valid: true, message: '' },
        },
      }))
    }
  }, [su.inviteToken, setFormState])

  const buttonText = su.isFetching ? 'Signing you up...' : 'Signup'
  const buttonDisabled = formInvalid || su.isFetching

  const renderErrors = () => {
    const allErrors = (errors as unknown[]) || []
    if (!(allErrors.length > 0)) {
      return null
    }
    const filteredErrors = filter(allErrors, (_err: unknown) => {
      return true
    })
    return (
      <div>
        {filteredErrors.map((error: unknown, idx: number) => (
          <Alert key={idx} onDismiss={() => dispatch(dismissSignupError(idx))}>
            {String(error)}
          </Alert>
        ))}
      </div>
    )
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormGroup error={validationState('username') === 'error'}>
          <FormLabel>Username</FormLabel>
          <FormControl
            required
            type="text"
            value={fieldValue('username')}
            placeholder="username..."
            onChange={handleFieldChange.bind(null, 'username')}
          />
          <FormHelp>{fieldMessage('username')}</FormHelp>
        </FormGroup>
        <FormGroup error={validationState('email') === 'error'}>
          <FormLabel>Email</FormLabel>
          <FormControl
            required
            type="email"
            value={fieldValue('email')}
            placeholder="test@example.com..."
            onChange={handleFieldChange.bind(null, 'email')}
          />
          <FormHelp>{fieldMessage('email')}</FormHelp>
        </FormGroup>
        <FormGroup error={validationState('password') === 'error'}>
          <FormLabel>Password</FormLabel>
          <FormControl
            required
            type="password"
            value={fieldValue('password')}
            placeholder="password..."
            onChange={handleFieldChange.bind(null, 'password')}
          />
          <FormHelp>{fieldMessage('password')}</FormHelp>
        </FormGroup>
        <FormGroup error={validationState('confirmPassword') === 'error'}>
          <FormLabel>Confirm password</FormLabel>
          <FormControl
            required
            type="password"
            value={fieldValue('confirmPassword')}
            placeholder="password again..."
            onChange={handleFieldChange.bind(null, 'confirmPassword')}
          />
          <FormHelp>{fieldMessage('confirmPassword')}</FormHelp>
        </FormGroup>
        <FormGroup>
          <input required type="hidden" value={fieldValue('inviteToken')} />
        </FormGroup>
        <Button type="submit" disabled={buttonDisabled}>
          {buttonText}
        </Button>
      </form>
      {renderErrors()}
    </div>
  )
}

export default SignupForm
